import React, { useEffect, useMemo } from "react";

import { ArrowLeft } from "lucide-react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import Badge from "../../Components/Badge";
import Button from "../../Components/Button";
import PageHeader from "../../Components/PageHeader";
import BookmarkButton from "../../Components/sponsor-tips/BookmarkButton";
import CategoryBadges from "../../Components/sponsor-tips/CategoryBadges";
import TipCard from "../../Components/sponsor-tips/TipCard";
import TipMarkdownContent from "../../Components/sponsor-tips/TipMarkdownContent";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  clearSponsorTipDetail,
  fetchSponsorTipBySlug,
  fetchSponsorTipsBookmarks,
} from "../../Store/Reducers/SponsorTipsSlice";
import {
  buildLibraryReturnHref,
  categoryDescription,
  formatAccountLevelMentionsInText,
  formatEbFeaturePreview,
  formatLevelTags,
  formatPublished,
  formatRoleLensTags,
  formatSeniorityTags,
  getLibraryViewState,
  inferLibraryReturnStateFromTip,
  getPairedGuidanceCta,
  LIBRARY_ARTICLE_TYPE,
  LIBRARY_DISPLAY_NAME,
  META_CHIP_CLASS,
  ROLE_LENS_CHIP_CLASS,
  TELEMETRY_EVENTS,
  trackSponsorTipsEvent,
  type LibraryReturnState,
} from "../../Utils/sponsorTipsConstants";

const ProseParagraphs: React.FC<{
  text: string;
  className?: string;
  paragraphClassName?: string;
}> = ({
  text,
  className,
  paragraphClassName = "mb-4 last:mb-0 whitespace-pre-wrap",
}) => {
  if (!text) return null;
  return (
    <div className={className}>
      {text.split("\n\n").map((para, i) => (
        <p key={i} className={paragraphClassName}>
          {para}
        </p>
      ))}
    </div>
  );
};

const SponsorTipDetailContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { slug, orgSlug } = useParams<{ slug: string; orgSlug?: string }>();
  const location = useLocation();
  const locationState = location.state as {
    fromLibrary?: LibraryReturnState;
    fromSubtab?: string;
  } | null;

  const {
    data,
    loading,
    error,
    slug: loadedSlug,
  } = useAppSelector(state => state.sponsorTips.detail);

  const isCurrentSlug = loadedSlug === slug;
  const tip = isCurrentSlug ? data?.tip : null;

  const fromLibrary: LibraryReturnState = useMemo(() => {
    if (locationState?.fromLibrary) return locationState.fromLibrary;
    if (tip) return inferLibraryReturnStateFromTip(tip);
    return {
      ...getLibraryViewState(),
      ...(locationState?.fromSubtab
        ? { subtab: locationState.fromSubtab }
        : {}),
    };
  }, [locationState, tip]);

  const libraryPath = orgSlug
    ? `/${orgSlug}/guidance-library`
    : "/guidance-library";
  const libraryHref = buildLibraryReturnHref(libraryPath, fromLibrary);

  const handleBack = () => {
    navigate(libraryHref, { state: { restoreLibrary: fromLibrary } });
  };

  const headerTitle = (
    <div className="flex items-center gap-2">
      <Button
        variant="clear"
        onClick={handleBack}
        className="min-w-0 p-1"
        aria-label="Go back"
      >
        <ArrowLeft size={15} color="#5F6368" />
      </Button>
      <span>{LIBRARY_DISPLAY_NAME}</span>
    </div>
  );

  useEffect(() => {
    if (!slug) return;
    trackSponsorTipsEvent(TELEMETRY_EVENTS.TIP_DETAIL_VIEWED, { slug });
    dispatch(fetchSponsorTipBySlug(slug));
    dispatch(fetchSponsorTipsBookmarks());
    return () => {
      dispatch(clearSponsorTipDetail());
    };
  }, [slug, dispatch]);

  const paired = isCurrentSlug ? (data?.paired_tip ?? null) : null;
  const moreLike = isCurrentSlug ? (data?.more_like_this ?? []) : [];

  if (loading || (slug && !isCurrentSlug)) {
    return (
      <div className="mt-3 flex min-h-0 flex-col overflow-hidden bg-white">
        <PageHeader title={headerTitle} hideIcon hideBreadcrumb />
        <div className="flex justify-center py-16">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#974ae4] border-t-transparent" />
        </div>
      </div>
    );
  }

  if (error || !tip || !orgSlug) {
    return (
      <div className="mt-3 flex min-h-0 flex-col overflow-hidden bg-white">
        <PageHeader title={headerTitle} hideIcon hideBreadcrumb />
        <div className="w-full min-h-0 flex-1 bg-white p-3 md:p-4">
          <div className="rounded-md bg-gray-100 p-4 md:p-6">
            <p className="text-sm text-red-600">{error || "Tip not found."}</p>
          </div>
        </div>
      </div>
    );
  }

  const isFm = tip.tip_type === "failure_mode";
  const hasMarkdown = Boolean(tip.body_markdown);
  const body = formatAccountLevelMentionsInText(tip.body);
  const published = formatPublished(tip.published_at);
  const levels = formatLevelTags(tip);
  const seniorities = formatSeniorityTags(tip);
  const roleLens = formatRoleLensTags(tip);
  const showLevelSeniority = roleLens.length === 0;
  const ebFeatureLabel = formatEbFeaturePreview(tip.eb_feature_preview);
  const catDesc = categoryDescription(tip.category);
  const pairedBody = formatAccountLevelMentionsInText(
    paired?.email_short || paired?.body || ""
  );

  return (
    <div className="mt-3 flex min-h-0 flex-col overflow-hidden bg-white">
      <PageHeader title={headerTitle} hideIcon hideBreadcrumb />
      <div className="w-full min-h-0 flex-1 bg-white p-3 md:p-4">
        <div className="rounded-md bg-gray-100 p-4 md:p-6">
          <article className="overflow-hidden rounded-lg border border-[#EBEBEB] bg-white">
            <header className="border-b border-[#F6F6F6] px-4 py-4 sm:px-5 sm:py-5">
              <div className="mb-3">
                <CategoryBadges category={tip.category} isFm={isFm} />
              </div>

              <div className="mb-3 flex items-start justify-between gap-3">
                <h1 className="m-0 flex-1 text-xl font-bold text-black">
                  {formatAccountLevelMentionsInText(tip.title)}
                </h1>
                <BookmarkButton
                  slug={tip.url_slug}
                  className="mt-0.5 shrink-0"
                />
              </div>

              <div className="flex flex-wrap gap-1.5">
                <Badge
                  type={isFm ? "warning" : "completed-green"}
                  label={isFm ? "Common failure mode" : "Best practice"}
                />
                {roleLens.map(r => (
                  <span key={r} className={ROLE_LENS_CHIP_CLASS}>
                    {r}
                  </span>
                ))}
                {showLevelSeniority
                  ? levels.map(l => (
                      <span key={l} className={META_CHIP_CLASS}>
                        {l}
                      </span>
                    ))
                  : null}
                {showLevelSeniority
                  ? seniorities.map(s => (
                      <span key={s} className={META_CHIP_CLASS}>
                        {s}
                      </span>
                    ))
                  : null}
                {published ? (
                  <span className={META_CHIP_CLASS}>Published {published}</span>
                ) : null}
              </div>

              {catDesc ? (
                <p className="mb-0 mt-3 text-sm leading-relaxed text-gray-500">
                  {catDesc}
                </p>
              ) : null}
            </header>

            <div className="px-4 py-5 sm:px-5 sm:py-6">
              {body ? (
                <ProseParagraphs
                  text={body}
                  className={LIBRARY_ARTICLE_TYPE.articleBody}
                />
              ) : null}
              {hasMarkdown && tip.body_markdown ? (
                <TipMarkdownContent markdown={tip.body_markdown} />
              ) : null}

              {ebFeatureLabel ? (
                <div className="mt-6 rounded-lg border border-[rgba(151,74,228,0.2)] bg-[#f5ebff] px-4 py-3.5 text-sm text-[#5b2d91]">
                  <span className="font-medium">
                    ExecBridge will automate this with:{" "}
                  </span>
                  {ebFeatureLabel}
                </div>
              ) : null}

              {paired ? (
                <div
                  className={`mt-6 overflow-hidden rounded-lg border bg-gray-50 ${
                    isFm
                      ? "border-emerald-200 border-l-4 border-l-emerald-600"
                      : "border-amber-200 border-l-4 border-l-amber-500"
                  }`}
                >
                  <div className="px-4 py-4 sm:px-5 sm:py-5">
                    <p className={`${LIBRARY_ARTICLE_TYPE.pairedLabel} mb-2`}>
                      {isFm ? (
                        "Instead, do this"
                      ) : (
                        <>
                          <span className="mr-1" aria-hidden>
                            ⚠
                          </span>
                          A failure mode to watch for
                        </>
                      )}
                    </p>
                    <h2 className="m-0 mb-3 text-lg font-bold text-black">
                      {formatAccountLevelMentionsInText(paired.title)}
                    </h2>
                    <ProseParagraphs
                      text={pairedBody}
                      className={LIBRARY_ARTICLE_TYPE.pairedBody}
                      paragraphClassName="mb-3 last:mb-0 whitespace-pre-wrap"
                    />
                    <Link
                      to={`/${orgSlug}/guidance-library/${paired.url_slug}`}
                      state={{ fromLibrary }}
                      className="mt-4 inline-flex text-sm font-medium text-[#974ae4] hover:underline"
                    >
                      {getPairedGuidanceCta(isFm)}
                    </Link>
                  </div>
                </div>
              ) : null}
            </div>
          </article>

          {moreLike.length > 0 ? (
            <section className="mt-8 border-t border-gray-200 pt-6">
              <h2 className="mb-4 text-base font-bold text-black">
                More like this
              </h2>
              <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
                {moreLike.map(t => (
                  <TipCard
                    key={t.id}
                    tip={t}
                    compact
                    fromLibrary={fromLibrary}
                    showBookmark={false}
                  />
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const SponsorTipDetail: React.FC = () => <SponsorTipDetailContent />;

export default SponsorTipDetail;
